import { z } from 'zod';
/**
 * A food in the owner's library: something they buy, with what is in it.
 *
 * Stored as an ordinary row in `entries`, the same way a life event is —
 * `recorded_at` is when it was added, `note` is the owner's own words about
 * it, and the JSON `data` column holds the rest. No new table, no migration.
 *
 * The barcode also goes in `entries.external_id`, so the existing
 * `UNIQUE(node_id, external_id)` index keeps one product from being entered
 * twice — the database holds that rule, not only the form.
 *
 * Entering a food is a one-off. Its reason for being here is that eating it
 * afterwards is a search, not a form: the library is what makes a diary
 * cheap enough to keep.
 */
/**
 * What a food is measured in: grams for most things, millilitres for drinks.
 *
 * A drinks label gives its figures per 100 ml, not per 100 g, and treating
 * one as the other is a few percent off for milk and worse for oil or syrup.
 * Every amount belonging to a food — its nutrient basis and every portion —
 * is in this one unit, so nothing ever converts between the two.
 */
export const foodBasisSchema = z.enum(['g', 'ml']);
/**
 * Nutrients per 100 g (or 100 ml, per the food's `basis`), the unit every
 * label in Europe already prints.
 *
 * Only energy is required. A label always carries it, and it is the one
 * figure the diary cannot do without; the rest are often missing from a
 * lookup, and a missing figure stays missing rather than becoming a 0 that
 * reads as "contains none".
 *
 * Grams, except energy (kcal). Upper bounds are what is physically possible
 * in 100 g, so a typo like 250 g of protein is refused rather than stored.
 */
export const nutrientsPer100Schema = z.object({
    energyKcal: z.number().min(0).max(900),
    protein: z.number().min(0).max(100).optional(),
    carbohydrates: z.number().min(0).max(100).optional(),
    /** Of which sugars. */
    sugars: z.number().min(0).max(100).optional(),
    fat: z.number().min(0).max(100).optional(),
    /** Of which saturated. */
    saturatedFat: z.number().min(0).max(100).optional(),
    fibre: z.number().min(0).max(100).optional(),
    salt: z.number().min(0).max(100).optional(),
});
/**
 * A named amount of this food, in the owner's words: "1 slice", "a bowl",
 * "the whole pack".
 *
 * People do not eat in grams, and they do not all eat in the same units, so
 * the library never assumes one. Each portion says how much it is, in the
 * food's `basis`, which is all the diary needs to turn "2 slices" back into
 * nutrients.
 *
 * A portion has no id on purpose. The diary stores the amount it resolved to
 * and the label as it read at the time, never a pointer to a portion, so
 * renaming or removing one here cannot rewrite a day already logged.
 */
export const portionSchema = z.object({
    label: z.string().trim().min(1).max(40),
    amount: z.number().positive().max(5000),
});
/**
 * True when the last digit is the GS1 check digit for the rest — the same
 * rule for EAN-8, UPC-A, EAN-13 and GTIN-14. Catches nearly every one-digit
 * typo and swapped pair, which is what a typed-in barcode goes wrong by.
 */
export function hasValidCheckDigit(digits) {
    if (!/^\d{2,}$/.test(digits))
        return false;
    const body = digits.slice(0, -1);
    let sum = 0;
    for (let i = 0; i < body.length; i++) {
        // Weights alternate 3, 1, 3, … counting from the digit next to the check digit.
        const weight = (body.length - i) % 2 === 1 ? 3 : 1;
        sum += Number(body[i]) * weight;
    }
    return (10 - (sum % 10)) % 10 === Number(digits[digits.length - 1]);
}
/**
 * One product, one spelling.
 *
 * The same pack can reach us as 12-digit UPC-A or as the 13-digit EAN with a
 * leading zero — a scanner reports whichever reader matched, and a person
 * types whatever is printed. Stored as given, those are two `external_id`s,
 * the duplicate check misses, and one product splits across two rows. So
 * every barcode is brought to one form before it is compared or stored:
 * UPC-A is zero-padded to EAN-13, and a GTIN-14 whose leading digit is 0 is
 * the EAN-13 inside it. EAN-8 is its own code and stays as it is.
 *
 * Returns null for anything that is not a barcode at all.
 */
export function normalizeBarcode(input) {
    const digits = input.replace(/[\s-]/g, '');
    if (!/^\d+$/.test(digits))
        return null;
    let code = digits;
    if (code.length === 12)
        code = `0${code}`;
    if (code.length === 14 && code.startsWith('0'))
        code = code.slice(1);
    if (code.length !== 8 && code.length !== 13 && code.length !== 14)
        return null;
    return hasValidCheckDigit(code) ? code : null;
}
/**
 * A barcode as stored: already normalised, with a valid check digit. Parse
 * with `normalizeBarcode` first — this refuses the un-normalised spelling
 * rather than quietly fixing it, so a caller that forgot cannot store one.
 */
export const barcodeSchema = z
    .string()
    .refine((code) => normalizeBarcode(code) === code, 'that is not a valid barcode — check the digits');
/** Where a food's figures came from, so the screen can say so. */
export const foodOriginSchema = z.enum(['manual', 'openfoodfacts']);
/** What goes in `entries.data` for a food. The rest lives in real columns. */
export const foodPayloadSchema = z.object({
    name: z.string().trim().min(1).max(200),
    brand: z.string().trim().min(1).max(120).optional(),
    /** Mirrored into `entries.external_id`; see the note at the top. */
    barcode: barcodeSchema.optional(),
    /** A product photo. Only ever an https URL — never a data: URI in a row. */
    imageUrl: z.string().url().startsWith('https://').max(1000).optional(),
    basis: foodBasisSchema,
    nutrientsPer100: nutrientsPer100Schema,
    portions: z.array(portionSchema).max(20).default([]),
    /**
     * Looked up, or typed in. A looked-up food the owner then corrected stays
     * 'openfoodfacts' — the origin is where it started, not who touched it last.
     */
    origin: foodOriginSchema,
});
/**
 * What a client sends to create or replace a food.
 *
 * Shared by the route that validates it and the form that builds it, so the
 * two cannot disagree about what a valid food is.
 */
export const foodInputSchema = foodPayloadSchema.extend({
    note: z.string().trim().max(2000).optional(),
});
/** One food, as every layer above the database passes it around. */
export const foodSchema = foodPayloadSchema.extend({
    id: z.string(),
    /** When it was added to the library. From `entries.recorded_at`. */
    addedAt: z.string().datetime(),
    /** The owner's own words. From `entries.note`. */
    note: z.string().nullable(),
});
/**
 * What a barcode lookup hands back: a food not yet saved, for the owner to
 * check before it is.
 *
 * Deliberately NOT saved by the lookup. A crowd-sourced figure is a starting
 * point, and it only becomes the owner's once they have looked at it.
 * `nutrientsPer100` is partial because a product can be found with its
 * energy missing, and the form then asks for exactly that.
 */
export const foodDraftSchema = foodPayloadSchema.extend({
    nutrientsPer100: nutrientsPer100Schema.partial(),
});
/**
 * Folds text down to what two spellings of the same word have in common, for
 * matching only — never for storing or showing. "Crème fraîche" and "creme
 * fraiche" are one food to someone typing on a phone.
 */
export function normalizeFoodText(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * The foods matching what the owner typed, best first.
 *
 * Every word typed must start a word somewhere in the name or brand, so
 * "half mel" finds "Halfvolle melk" but "elk" does not. A barcode matches
 * when the query is a run of its digits, which is what someone does when a
 * scan fails and they type what is under the stripes.
 *
 * Ranked by: the name starting with the query, then every word matching in
 * the name alone, then the rest; ties alphabetical. An empty query returns
 * the whole library alphabetically, which is what a picker opens on.
 *
 * Runs over the whole library in memory. One person's groceries are a few
 * hundred rows, and a search box that answers on every keystroke matters more
 * than an index.
 */
export function searchFoods(foods, query) {
    const byName = (a, b) => a.name.localeCompare(b.name);
    const q = normalizeFoodText(query);
    if (q === '')
        return [...foods].sort(byName);
    const terms = q.split(' ');
    const digitsOnly = /^\d+$/.test(q);
    const scored = [];
    for (const food of foods) {
        const name = normalizeFoodText(food.name);
        const nameWords = name.split(/[^a-z0-9]+/).filter(Boolean);
        const allWords = [...nameWords, ...normalizeFoodText(food.brand ?? '').split(/[^a-z0-9]+/).filter(Boolean)];
        const startsWord = (words) => terms.every((term) => words.some((word) => word.startsWith(term)));
        let rank = null;
        if (name.startsWith(q))
            rank = 0;
        else if (startsWord(nameWords))
            rank = 1;
        else if (startsWord(allWords))
            rank = 2;
        else if (digitsOnly && food.barcode?.includes(q))
            rank = 3;
        if (rank !== null)
            scored.push({ food, rank });
    }
    return scored.sort((a, b) => a.rank - b.rank || byName(a.food, b.food)).map((s) => s.food);
}
//# sourceMappingURL=foods.js.map